/**
 * Feed Activity Service
 * 用于创建用户活动feed记录
 */

import { isLearnTest } from './utils';

/**
 * 服务端环境检测
 * 检查是否为测试环境（基于环境变量和主机名）
 */
function isServerTestEnvironment(): boolean {
  // 检查是否在服务端
  if (typeof window === 'undefined') {
    // 通过环境变量判断
    const nodeEnv = process.env.NODE_ENV;
    const publicEnv = process.env.NEXT_PUBLIC_ENVIRONMENT;

    // 如果是开发环境或明确标记为测试环境
    if (nodeEnv === 'development' || publicEnv === 'test') {
      return true;
    }

    // 通过 Vercel 或其他部署平台的环境变量判断
    const vercelUrl = process.env.VERCEL_URL || '';
    if (vercelUrl.includes('learn-test') || vercelUrl.includes('localhost')) {
      return true;
    }
  }

  return false;
}

export type FeedType =
  | 'learn_completed'
  | 'practice_completed'
  | 'event_completed'
  | 'adventure_completed'
  | 'minipet_completed'
  | 'fish_completed';

export interface FeedMetadata {
  title?: string;
  coverImage?: string;
  score?: number;
  duration?: number;
  [key: string]: any;
}

interface CreateFeedActivityParams {
  feedType: FeedType;
  contentId: string;
  metadata?: FeedMetadata;
}

/**
 * 创建feed活动记录
 * @param params - 活动参数
 * @param supabase - Supabase客户端实例
 * @returns Promise<{ success: boolean; activityId?: string; error?: string }>
 */
export async function createFeedActivity(
  params: CreateFeedActivityParams,
  supabase: any
): Promise<{ success: boolean; activityId?: string; error?: string }> {
  try {
    const { feedType, contentId, metadata = {} } = params;

    // 测试环境下不写入数据库
    if (isLearnTest) {
      console.log(`[Feed] Test environment detected, skipping ${feedType} activity creation:`, contentId);
      return { success: true, activityId: 'test-activity-id' };
    }

    // 获取当前用户
    const { data: { user }, error: userError } = await supabase.auth.getUser();

    if (userError || !user) {
      console.warn('[Feed] No authenticated user, skipping feed creation');
      return { success: false, error: 'No authenticated user' };
    }

    // 直接插入数据到 user_feed_activities 表
    const { data, error } = await supabase
      .from('user_feed_activities')
      .insert({
        user_id: user.id,
        feed_type: feedType,
        content_id: contentId,
        metadata: metadata
      })
      .select('id')
      .single();

    if (error) {
      console.error('[Feed] Failed to create feed activity:', error);
      return { success: false, error: error.message };
    }

    console.log(`[Feed] Created ${feedType} activity:`, contentId, data.id);
    return { success: true, activityId: data.id };

  } catch (error) {
    console.error('[Feed] Error creating feed activity:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * 服务端创建feed活动记录
 * 用于API路由中直接调用
 */
export async function createFeedActivityServer(
  params: CreateFeedActivityParams,
  supabase: any,
  userId: string
): Promise<{ success: boolean; activityId?: string; error?: string }> {
  try {
    const { feedType, contentId, metadata = {} } = params;

    // 测试环境下不写入数据库
    if (isServerTestEnvironment()) {
      console.log(`[Feed] Test environment detected (server), skipping ${feedType} activity creation:`, contentId);
      return { success: true, activityId: 'test-activity-id-server' };
    }

    if (!userId) {
      console.warn('[Feed] No userId provided, skipping feed creation');
      return { success: false, error: 'No userId provided' };
    }

    // 直接插入数据到 user_feed_activities 表
    const { data, error } = await supabase
      .from('user_feed_activities')
      .insert({
        user_id: userId,
        feed_type: feedType,
        content_id: contentId,
        metadata: metadata
      })
      .select('id')
      .single();

    if (error) {
      console.error('[Feed] Failed to create feed activity:', error);
      return { success: false, error: error.message };
    }

    console.log(`[Feed] Created ${feedType} activity:`, contentId, data.id);
    return { success: true, activityId: data.id };

  } catch (error) {
    console.error('[Feed] Error creating feed activity:', error);
    return { success: false, error: String(error) };
  }
}
